import { useState, useEffect, useCallback } from 'react';
import type {
  VendorInfo,
  VendorSuggestion,
  EntityMatch,
  VendorMatchCriteria,
  VendorMatchingRuleListItem,
} from '@acitra/shared';
import {
  getVendorSuggestions,
  matchVendor,
  unmatchVendor,
  listVendorMatchingRules,
  deleteVendorMatchingRule,
  type VendorListItem,
} from '../../services/api';
import { VendorSearchDropdown } from './VendorSearchDropdown';
import { MatchCriteriaConfig } from './MatchCriteriaConfig';
import { CreateVendorForm } from './CreateVendorForm';
import { useDraggable } from '../../hooks/useDraggable';

interface VendorMatchingModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  invoiceId: string;
  extractedVendor?: VendorInfo;
  effectiveCurrency?: string;
  currentMatch?: EntityMatch;
  onMatchUpdated: () => void;
}

type ModalView = 'search' | 'create';

export function VendorMatchingModal({
  isOpen,
  onClose,
  tenantId,
  invoiceId,
  extractedVendor,
  effectiveCurrency,
  currentMatch,
  onMatchUpdated,
}: VendorMatchingModalProps) {
  const [view, setView] = useState<ModalView>('search');
  const [suggestions, setSuggestions] = useState<VendorSuggestion[]>([]);
  const [matchedRules, setMatchedRules] = useState<VendorMatchingRuleListItem[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<VendorListItem | null>(null);
  const [rememberMatch, setRememberMatch] = useState(false);
  const [showCriteriaConfig, setShowCriteriaConfig] = useState(false);
  const [criteria, setCriteria] = useState<VendorMatchCriteria>({});
  const [isMatching, setIsMatching] = useState(false);
  const [isUnmatching, setIsUnmatching] = useState(false);
  const [isDeletingRule, setIsDeletingRule] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Draggable modal
  const { position, isDragging, handleMouseDown, reset: resetPosition } = useDraggable();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setView('search');
      setError(null);
      setRememberMatch(false);
      setShowCriteriaConfig(false);
      resetPosition();

      // Pre-populate with current match if editing
      if (currentMatch) {
        setSelectedVendor({
          id: currentMatch.entityId,
          quickbooksId: currentMatch.entityId,
          displayName: currentMatch.entityName,
          active: true,
          syncedAt: '',
        });
      } else {
        setSelectedVendor(null);
      }

      // Initialize criteria with smart defaults
      if (extractedVendor) {
        const defaultCriteria: VendorMatchCriteria = {};
        if (extractedVendor.name) {
          defaultCriteria.vendorNameContains = extractedVendor.name;
        }
        if (extractedVendor.accountNumber) {
          defaultCriteria.accountNumber = extractedVendor.accountNumber;
        }
        setCriteria(defaultCriteria);
      }
    }
  }, [isOpen, currentMatch, extractedVendor, resetPosition]);

  // Load suggestions and matching rules when modal opens
  const loadData = useCallback(async () => {
    if (!isOpen || !extractedVendor) return;

    setIsLoadingSuggestions(true);
    setError(null);

    try {
      // Load suggestions and rules in parallel
      const [suggestionsResponse, rulesResponse] = await Promise.all([
        getVendorSuggestions(tenantId, invoiceId),
        listVendorMatchingRules(tenantId).catch(() => [] as VendorMatchingRuleListItem[]),
      ]);

      setSuggestions(suggestionsResponse.suggestions);

      // Filter rules to only those that matched this vendor (by ruleId from suggestions)
      const matchedRuleIds = new Set(
        suggestionsResponse.suggestions
          .filter((s) => s.isRuleMatch && s.ruleId)
          .map((s) => s.ruleId)
      );
      setMatchedRules(rulesResponse.filter((rule) => matchedRuleIds.has(rule.id)));
    } catch (err) {
      console.error('Failed to load vendor data:', err);
      setError('Failed to load suggestions');
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [isOpen, tenantId, invoiceId, extractedVendor]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelectSuggestion = (suggestion: VendorSuggestion) => {
    setSelectedVendor({
      id: suggestion.vendorId,
      quickbooksId: suggestion.vendorId,
      displayName: suggestion.displayName,
      companyName: suggestion.companyName,
      active: true,
      syncedAt: '',
    });
  };

  const handleSelectFromSearch = (vendor: VendorListItem) => {
    setSelectedVendor(vendor);
  };

  const handleMatch = async () => {
    if (!selectedVendor) return;

    setIsMatching(true);
    setError(null);
    try {
      await matchVendor(tenantId, invoiceId, {
        vendorId: selectedVendor.quickbooksId,
        createRule: rememberMatch && Object.keys(criteria).length > 0 ? { criteria } : undefined,
      });
      onMatchUpdated();
    } catch (err) {
      console.error('Failed to match vendor:', err);
      setError('Failed to match vendor');
    } finally {
      setIsMatching(false);
    }
  };

  const handleUnmatch = async () => {
    setIsUnmatching(true);
    setError(null);
    try {
      await unmatchVendor(tenantId, invoiceId);
      setSelectedVendor(null);
      onMatchUpdated();
    } catch (err) {
      console.error('Failed to unmatch vendor:', err);
      setError('Failed to unmatch vendor');
    } finally {
      setIsUnmatching(false);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    setIsDeletingRule(ruleId);
    try {
      await deleteVendorMatchingRule(tenantId, ruleId);
      setMatchedRules((prev) => prev.filter((r) => r.id !== ruleId));
    } catch (err) {
      console.error('Failed to delete rule:', err);
      setError('Failed to delete rule');
    } finally {
      setIsDeletingRule(null);
    }
  };

  // Format extracted vendor summary
  const extractedSummary = extractedVendor
    ? [
        extractedVendor.name,
        extractedVendor.email,
        extractedVendor.accountNumber ? `Acct: ${extractedVendor.accountNumber}` : null,
      ]
        .filter(Boolean)
        .join(' | ')
    : 'No vendor information';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-15 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-xl rounded-lg bg-white shadow-xl"
          style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
          }}
        >
          {/* Header - drag handle */}
          <div
            className={`flex items-center justify-between border-b border-gray-200 px-6 py-4 select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onMouseDown={handleMouseDown}
          >
            {/* Drag handle icon */}
            <div className="flex items-center gap-3">
              <svg className="w-4 h-4 text-gray-400" viewBox="0 0 16 16" fill="currentColor">
                <circle cx="4" cy="3" r="1.5" />
                <circle cx="12" cy="3" r="1.5" />
                <circle cx="4" cy="8" r="1.5" />
                <circle cx="12" cy="8" r="1.5" />
                <circle cx="4" cy="13" r="1.5" />
                <circle cx="12" cy="13" r="1.5" />
              </svg>
              <h2 className="text-lg font-semibold text-gray-900">
                {view === 'search' ? 'Match Vendor to QuickBooks' : 'Create New Vendor'}
              </h2>
            </div>
            <button
              onClick={onClose}
              onMouseDown={(e) => e.stopPropagation()}
              className="text-gray-400 hover:text-gray-500 cursor-pointer"
              aria-label="Close"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 max-h-[80vh] overflow-y-auto">
            {view === 'search' ? (
              <>
                {/* Extracted vendor summary */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Extracted from invoice
                  </p>
                  <div className="bg-gray-50 rounded-md px-3 py-2">
                    <p className="text-sm text-gray-700">{extractedSummary}</p>
                  </div>
                </div>

                {/* Search dropdown */}
                <div className="mb-4">
                  <VendorSearchDropdown
                    tenantId={tenantId}
                    selectedVendor={selectedVendor}
                    onSelect={handleSelectFromSearch}
                  />
                </div>

                {/* Selected vendor display */}
                {selectedVendor && (
                  <div className="mb-4 flex items-center justify-between p-3 bg-primary-50 rounded-md border border-primary-200">
                    <div>
                      <p className="font-medium text-gray-900">{selectedVendor.displayName}</p>
                      {selectedVendor.companyName &&
                        selectedVendor.companyName !== selectedVendor.displayName && (
                          <p className="text-xs text-gray-500">{selectedVendor.companyName}</p>
                        )}
                    </div>
                    <button
                      onClick={() => setSelectedVendor(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Suggestions */}
                {!selectedVendor && (
                  <>
                    {isLoadingSuggestions ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
                        Loading suggestions...
                      </div>
                    ) : suggestions.length > 0 ? (
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                          Suggestions
                        </p>
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                          {suggestions.map((suggestion) => (
                            <button
                              key={suggestion.vendorId}
                              onClick={() => handleSelectSuggestion(suggestion)}
                              className="w-full text-left px-3 py-2 rounded-md border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {suggestion.displayName}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    Matched: {suggestion.matchedFields.join(', ')}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`text-sm font-medium ${
                                      suggestion.confidence >= 80
                                        ? 'text-emerald-600'
                                        : suggestion.confidence >= 50
                                          ? 'text-amber-600'
                                          : 'text-gray-500'
                                    }`}
                                  >
                                    {suggestion.confidence}%
                                  </span>
                                  {suggestion.isRuleMatch && (
                                    <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded">
                                      Rule
                                    </span>
                                  )}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </>
                )}

                {/* Remember match checkbox */}
                {selectedVendor && extractedVendor && (
                  <div className="mb-4 pt-4 border-t border-gray-200">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rememberMatch}
                        onChange={(e) => setRememberMatch(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">
                        Remember this match for future invoices
                      </span>
                    </label>

                    {rememberMatch && (
                      <div className="mt-2 ml-6">
                        <button
                          onClick={() => setShowCriteriaConfig(!showCriteriaConfig)}
                          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                        >
                          <svg
                            className={`w-4 h-4 transition-transform ${showCriteriaConfig ? 'rotate-90' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                          Configure matching criteria...
                        </button>

                        {showCriteriaConfig && (
                          <div className="mt-2">
                            <MatchCriteriaConfig
                              extractedVendor={extractedVendor}
                              effectiveCurrency={effectiveCurrency}
                              criteria={criteria}
                              onCriteriaChange={setCriteria}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Matched rules section */}
                {matchedRules.length > 0 && (
                  <div className="mb-4 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                      Matched Rules ({matchedRules.length})
                    </p>
                    <div className="space-y-2">
                      {matchedRules.map((rule) => (
                        <div
                          key={rule.id}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-md text-sm"
                        >
                          <span className="text-gray-700">
                            {rule.description || formatRuleCriteria(rule.criteria)} &rarr;{' '}
                            {rule.targetVendorName}
                          </span>
                          <button
                            onClick={() => handleDeleteRule(rule.id)}
                            disabled={isDeletingRule === rule.id}
                            className="text-red-500 hover:text-red-700 text-xs disabled:opacity-50"
                          >
                            {isDeletingRule === rule.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Create new vendor link */}
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setView('create')}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Create new vendor in QuickBooks
                  </button>
                </div>
              </>
            ) : (
              // Create vendor form view
              <CreateVendorForm
                tenantId={tenantId}
                extractedVendor={extractedVendor}
                onVendorCreated={(vendor) => {
                  // Auto-select the newly created vendor and go back to search view
                  setSelectedVendor(vendor);
                  setView('search');
                }}
                onCancel={() => setView('search')}
              />
            )}

            {/* Error display */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>

          {/* Footer - only show for search view (create form has its own footer) */}
          {view === 'search' && (
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              {currentMatch && (
                <button
                  onClick={handleUnmatch}
                  disabled={isUnmatching}
                  className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50"
                >
                  {isUnmatching ? 'Unmatching...' : 'Unmatch'}
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleMatch}
                disabled={!selectedVendor || isMatching}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isMatching ? 'Matching...' : 'Confirm Match'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper function to format rule criteria for display
function formatRuleCriteria(criteria: VendorMatchCriteria): string {
  const parts: string[] = [];
  if (criteria.vendorNameContains) parts.push(`"${criteria.vendorNameContains}"`);
  if (criteria.vendorNameExact) parts.push(`exactly "${criteria.vendorNameExact}"`);
  if (criteria.accountNumber) parts.push(`acct ${criteria.accountNumber}`);
  if (criteria.email) parts.push(criteria.email);
  if (criteria.phone) parts.push(criteria.phone);
  if (criteria.postalCode) parts.push(`zip ${criteria.postalCode}`);
  return parts.join(' + ') || 'No criteria';
}
